import {
  Button,
  Dialog,
  Label,
  MoneyInput,
  parseEurInput,
  SelectField,
  TextField,
} from "@gladis/ui";
import { errorSentence, useT } from "@gladis/ui/i18n";
import { useState, type ChangeEvent, type FormEvent, type ReactNode } from "react";

import { useRecordValuation } from "../hooks.js";

/**
 * Record what a vehicle is worth.
 *
 * ==========================================================================================
 * BACKDATING IS THE POINT, NOT AN ESCAPE HATCH
 * ==========================================================================================
 *
 * The date defaults to today and is freely editable, and the hint says why: entering the purchase
 * price at the purchase date and a current figure today gives the household TWO observations
 * spanning years on the day they adopt the module — which is what makes a depreciation figure
 * available immediately rather than a year from now.
 *
 * There is no warning for a past date. Savings' rule, and its reason: warning about the normal case
 * trains people to dismiss warnings, and then the one that matters goes with it.
 *
 * ==========================================================================================
 * THE SOURCE IS TWO VALUES, AND BOTH MEAN "A PERSON LOOKED"
 * ==========================================================================================
 *
 * `koerslijst` is a figure read off ANWB's free tool; `manual` is the household's own judgement.
 * There is deliberately no third option — nothing observes a car for free, so a third would mean "a
 * number this application made up". The hint points at where to get the first one, because a prompt
 * that says a valuation is stale and does not say where to look is only half a prompt.
 */
export function ValuationForm({
  open,
  onClose,
  vehicleId,
  vehicleName,
  firstRegisteredOn,
  onRecorded,
}: {
  open: boolean;
  onClose: () => void;
  vehicleId: string;
  vehicleName: string;
  /** Bounds the date, so the form can refuse before the server has to. */
  firstRegisteredOn: string;
  onRecorded?: () => void;
}): ReactNode {
  const t = useT();
  const today = () => new Date().toISOString().slice(0, 10);

  const [amount, setAmount] = useState("");
  const [valuedOn, setValuedOn] = useState(today);
  const [source, setSource] = useState<"manual" | "koerslijst">("koerslijst");

  /*
   * WHICH VEHICLE THIS DIALOG IS ABOUT, CAPTURED WHEN IT OPENS.
   *
   * The caller drops its selection the moment the dialog closes, so between that state update and
   * the close there is one render with no vehicle in hand — long enough to paint a heading reading
   * "What is worth?" with nothing in the middle. `RecordBalanceForm` records the same trap.
   */
  const [target, setTarget] = useState({
    id: vehicleId,
    name: vehicleName,
    from: firstRegisteredOn,
  });

  const [wasOpen, setWasOpen] = useState(false);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setTarget({ id: vehicleId, name: vehicleName, from: firstRegisteredOn });
      setAmount("");
      setValuedOn(today());
      setSource("koerslijst");
    }
  }

  const record = useRecordValuation();

  function submit(event: FormEvent): void {
    event.preventDefault();
    const euros = parseEurInput(amount);
    if (euros === null) return;

    record.mutate(
      { vehicleId: target.id, valuedOn, amountInEuros: euros, source },
      {
        onSuccess: () => {
          onRecorded?.();
          onClose();
        },
      },
    );
  }

  return (
    <Dialog open={open} onClose={onClose} title={t("veh.valuation.heading", { name: target.name })}>
      <form onSubmit={submit} className="flex flex-col gap-4">
        <div>
          <Label htmlFor="valuation-amount">{t("veh.valuation.amount")}</Label>
          <MoneyInput
            id="valuation-amount"
            value={amount}
            // `onValueChange`, not `onChange`: the control only reports text that is a valid partial
            // amount, so an invalid keystroke never reaches this state at all.
            onValueChange={setAmount}
            required
          />
        </div>

        <div>
          <Label htmlFor="valuation-date">{t("veh.valuation.date")}</Label>
          <TextField
            id="valuation-date"
            type="date"
            value={valuedOn}
            min={target.from}
            max={today()}
            onChange={(event) => {
              setValuedOn(event.target.value);
            }}
            required
          />
          <p className="mt-1 text-xs text-muted">{t("veh.valuation.dateHint")}</p>
        </div>

        <div>
          <Label htmlFor="valuation-source">{t("veh.valuation.source")}</Label>
          <SelectField
            id="valuation-source"
            value={source}
            onChange={(event: ChangeEvent<HTMLSelectElement>) => {
              setSource(event.target.value === "manual" ? "manual" : "koerslijst");
            }}
          >
            <option value="koerslijst">{t("veh.valuation.sourceKoerslijst")}</option>
            <option value="manual">{t("veh.valuation.sourceManual")}</option>
          </SelectField>
          <p className="mt-1 text-xs text-muted">{t("veh.valuation.sourceHint")}</p>
        </div>

        {record.error ? <p className="text-sm text-danger">{errorSentence(record.error)}</p> : null}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" pending={record.isPending}>
            {t("veh.valuation.submit")}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
